import { skipToken, useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { toast } from 'sonner';

import { useWorkspace } from '@/_shared/contexts/WorkspaceContext';
import { useOrpc } from '@/_shared/hooks/useOrpc';
import type { SSEMessage } from '@/_shared/types';

import { useSSE } from '../../chat/hooks/useSSE';
import { filterOutTurn } from '../../chat/lib/turn-slicing';
import { findUserMessageInTurn } from '../../chat/lib/turn-utils';
import { type PanelDataSummary, summarizePanelData } from '../lib/panel-data-summary';
import type { Dashboard } from '../types';

interface PanelQueryResult {
  columns: string[];
  rows: Record<string, unknown>[];
  rowCount: number | null;
}

export interface UseDashboardChatReturn {
  isOpen: boolean;
  open: () => void;
  close: () => void;
  toggle: () => void;
  sessionId: string | null;
  messages: SSEMessage[];
  connected: boolean;
  error: string | null;
  sendMessage: (content: string) => void;
  resetChat: () => void;
  agentActive: boolean;
  /** True when session has been created/loaded and messages can be sent. */
  sessionReady: boolean;
  /** Open the sidebar and send a message, queuing if the session isn't ready yet. */
  openAndSend: (message: string) => void;
  /** Fire the abort for the active turn. No-op when no turn is active. */
  stopTurn: () => void;
  /** True when the agent is active AND a turn ID has been confirmed — safe to show the Stop button. */
  canStop: boolean;
  /** The user message of the most recently aborted turn — for restoring into the textarea. */
  prefillValue: string | undefined;
  /** Bumps each time a new prefill should be applied. */
  prefillNonce: number;
}

export function useDashboardChat(
  dashboard: Dashboard | null,
  paramValues: Record<string, string | string[]>,
): UseDashboardChatReturn {
  const [isOpen, setIsOpen] = useState(false);
  const [sessionId, setSessionId] = useState<string | null>(null);
  const [agentActive, setAgentActive] = useState(false);
  const [activeTurnId, setActiveTurnId] = useState<string | null>(null);
  const [prefillValue, setPrefillValue] = useState<string | undefined>(undefined);
  const [prefillNonce, setPrefillNonce] = useState(0);
  const queryClient = useQueryClient();
  const { slug } = useWorkspace();
  const orpc = useOrpc();
  const initializedForSession = useRef<string | null>(null);

  // Reset all state when the dashboard changes (render-phase pattern)
  const dashboardId = dashboard?.id ?? null;
  const [prevDashboardId, setPrevDashboardId] = useState(dashboardId);
  if (dashboardId !== prevDashboardId) {
    setPrevDashboardId(dashboardId);
    setSessionId(null);
    setAgentActive(false);
    setActiveTurnId(null);
    setPrefillValue(undefined);
    setPrefillNonce(0);
  }

  // Fetch warehouse info for the dashboard context envelope
  const { data: warehouseData } = useQuery(orpc.settings.warehouses.list.queryOptions({ input: {} }));
  const warehouses = useMemo(() => warehouseData?.warehouses ?? [], [warehouseData?.warehouses]);

  // Lazy getOrCreate — only fires when sidebar is first opened
  const { data: sessionData, refetch: refetchSession } = useQuery(
    orpc.dashboardChat.getOrCreate.queryOptions({
      input: dashboard && isOpen ? { dashboardId: dashboard.id } : skipToken,
    }),
  );

  // Sync session ID from server data (render-phase pattern)
  const serverSessionId = sessionData?.session?.id ?? null;
  const [prevServerSessionId, setPrevServerSessionId] = useState(serverSessionId);
  if (serverSessionId !== prevServerSessionId) {
    setPrevServerSessionId(serverSessionId);
    if (serverSessionId && serverSessionId !== sessionId) {
      setSessionId(serverSessionId);
    }
  }

  // Stable refs for SSE callbacks to access current state without re-creating the hook
  const dashboardSlugRef = useRef(dashboard?.slug);
  useEffect(() => {
    dashboardSlugRef.current = dashboard?.slug;
  });

  // SSE connection with event/disconnect callbacks to avoid set-state-in-effect
  const handleSSEEvent = useCallback(
    (eventType: string, data: Record<string, unknown>) => {
      if (eventType === 'turn_complete' || eventType === 'error') {
        setAgentActive(false);
        setActiveTurnId(null);
        // Refresh dashboard + panel data + dynamic param options in case the agent edited it
        const slug = dashboardSlugRef.current;
        if (slug) {
          void queryClient.invalidateQueries({
            queryKey: orpc.dashboard.get.queryOptions({ input: { slug } }).queryKey,
          });
          void queryClient.invalidateQueries({
            queryKey: orpc.dashboard.query.key(),
          });
          void queryClient.invalidateQueries({
            queryKey: orpc.dashboard.paramOptions.key(),
          });
        }
      }
      if (eventType === 'turn_aborted') {
        const userMessage = typeof data['userMessage'] === 'string' ? data['userMessage'] : '';
        if (userMessage) {
          setPrefillValue(userMessage);
          setPrefillNonce((n) => n + 1);
        }
        setAgentActive(false);
        setActiveTurnId(null);
        // Invalidate dashboard data — the agent may have saved changes mid-turn
        // before the user clicked stop, so we need to refresh the dashboard view.
        const slug = dashboardSlugRef.current;
        if (slug) {
          void queryClient.invalidateQueries({
            queryKey: orpc.dashboard.get.queryOptions({ input: { slug } }).queryKey,
          });
          void queryClient.invalidateQueries({
            queryKey: orpc.dashboard.query.key(),
          });
          void queryClient.invalidateQueries({
            queryKey: orpc.dashboard.paramOptions.key(),
          });
        }
      }
    },
    [queryClient, orpc],
  );

  // Don't clear agentActive on disconnect — the SSE will retry and reconnect.
  // If the turn completed during the gap, the replayed turn_complete/error
  // event will clear agentActive. Clearing here would falsely unlock the UI
  // while the agent is still running server-side.
  const handleSSEDisconnect = useCallback(() => {
    // intentionally empty — agentActive is managed by SSE events, not connectivity
  }, []);

  const sseOptions = useMemo(
    () => ({
      onEvent: handleSSEEvent,
      onDisconnect: handleSSEDisconnect,
    }),
    [handleSSEEvent, handleSSEDisconnect],
  );

  // SSE connection — turn-scoped: only connect when there's an active turn to follow
  const knownTurnId = activeTurnId ?? sessionData?.runtime?.activeTurn?.turnId ?? null;
  const streamUrl =
    sessionId && knownTurnId && slug
      ? `/api/w/${encodeURIComponent(slug)}/chats/${sessionId}/turns/${knownTurnId}/events`
      : null;
  const {
    messages,
    connected,
    error: sseError,
    addMessage,
    removeLastUserMessage,
    initializeMessages,
    clearMessages,
    forceTurnAborted,
  } = useSSE(streamUrl, sseOptions);

  // Terminal fallback: if SSE exhausts all retries, unlock the UI so the user
  // can retry or reset the chat. Without this, agentActive stays true forever.
  // Guard: only act when connected=false AND error is set AND no activeTurnId
  // is pending (once activeTurnId is set, streamUrl changes, which resets the
  // error in useSSE). If activeTurnId is null but agentActive is true, the
  // mutation hasn't returned yet — don't clear.
  useEffect(() => {
    if (agentActive && activeTurnId && sseError?.startsWith('Connection lost')) {
      setAgentActive(false);
      setActiveTurnId(null);
    }
  }, [agentActive, activeTurnId, sseError]);

  // Clear messages when sessionId resets (dashboard switch)
  useEffect(() => {
    if (!sessionId) clearMessages();
  }, [sessionId, clearMessages]);

  // Initialize from historical messages (once per session).
  // Filter out events belonging to the active turn — the SSE stream owns those.
  // Race-safe: if SSE delivered events before sessionData arrived, prepend
  // historical messages instead of replacing.
  const messagesRef = useRef(messages);
  useEffect(() => {
    messagesRef.current = messages;
  });

  useEffect(() => {
    if (sessionId && sessionData?.sseMessages && initializedForSession.current !== sessionId) {
      initializedForSession.current = sessionId;
      const filtered = knownTurnId ? filterOutTurn(sessionData.sseMessages, knownTurnId) : sessionData.sseMessages;
      const currentSSE = messagesRef.current;

      if (currentSSE.length > 0) {
        initializeMessages([...filtered, ...currentSSE]);
      } else {
        initializeMessages(filtered);
      }
    }
  }, [sessionId, sessionData?.sseMessages, knownTurnId, initializeMessages]);

  // Restore aborted prompt on initial session load — if the tail event is
  // turn_aborted and user reloads, put the prompt back into the composer.
  const prefillRestoredRef = useRef<string | null>(null);
  useEffect(() => {
    const prompt = sessionData?.runtime?.lastAbortedPrompt;
    if (prompt && sessionId && prefillRestoredRef.current !== sessionId) {
      prefillRestoredRef.current = sessionId;
      setPrefillValue(prompt);
      setPrefillNonce((n) => n + 1);
    }
  }, [sessionData?.runtime?.lastAbortedPrompt, sessionId]);

  // stopTurnMutation optimistically unlocks the composer on stop click (R1 + R4).
  interface StopTurnVars {
    id: string;
    turnId: string;
  }
  interface StopTurnCtx {
    snapshot: {
      agentActive: boolean;
      activeTurnId: string | null;
      prefillValue: string | undefined;
      prefillNonce: number;
    };
  }
  const stopTurnMutation = useMutation<{ ok: boolean }, Error, StopTurnVars, StopTurnCtx>({
    mutationFn: (input) => orpc.dashboardChat.stopTurn.call(input),
    onMutate: (vars) => {
      const { turnId } = vars;
      const snapshot = { agentActive, activeTurnId, prefillValue, prefillNonce };

      // Find user_message: try turn-scoped first, fall back to last in list
      // (dashboard sidebar adds optimistic user_message before turn_start arrives)
      let userMessage = findUserMessageInTurn(messages, turnId);
      if (!userMessage) {
        for (let i = messages.length - 1; i >= 0; i--) {
          const m = messages[i];
          if (m?.type === 'user_message') {
            userMessage = m.content;
            break;
          }
        }
      }

      forceTurnAborted(turnId, userMessage ?? '');
      if (userMessage) {
        setPrefillValue(userMessage);
        setPrefillNonce((n) => n + 1);
      }
      setAgentActive(false);
      setActiveTurnId(null);
      return { snapshot };
    },
    onError: (err, _vars, ctx) => {
      if (ctx?.snapshot) {
        setAgentActive(ctx.snapshot.agentActive);
        setActiveTurnId(ctx.snapshot.activeTurnId);
        setPrefillValue(ctx.snapshot.prefillValue);
        setPrefillNonce(ctx.snapshot.prefillNonce);
      }
      const msg = err instanceof Error ? err.message : 'Failed to stop the agent. Please try again.';
      toast.error(msg);
    },
  });

  const stopTurn = useCallback(() => {
    if (sessionId && knownTurnId) {
      stopTurnMutation.mutate({ id: sessionId, turnId: knownTurnId });
    }
  }, [sessionId, knownTurnId, stopTurnMutation]);

  // Send message mutation
  const sendMessageMutation = useMutation(orpc.dashboardChat.sendMessage.mutationOptions());

  // Gather dashboard context from React Query cache
  const gatherDashboardContext = useCallback((): {
    title: string;
    description: string | null;
    slug: string;
    warehouseId: string;
    warehouseName: string;
    warehouseType: string;
    params: Record<string, string | string[]>;
    panels: PanelDataSummary[];
  } | null => {
    if (!dashboard) return null;

    const wh = warehouses.find((w) => w.rudderDestinationId === dashboard.warehouseId);

    const panelSummaries: PanelDataSummary[] = dashboard.spec.panels.map((panel) => {
      // Read from React Query cache — same query key as PanelWithData uses
      const queryKey = orpc.dashboard.query.queryOptions({
        input: { dashboardId: dashboard.id, panelId: panel.id, params: paramValues },
      }).queryKey;
      const cached = queryClient.getQueryData<PanelQueryResult>(queryKey);
      const errorState = queryClient.getQueryState(queryKey)?.error;

      return summarizePanelData(
        panel,
        cached ?? null,
        errorState instanceof Error ? errorState.message : errorState ? String(errorState) : null,
      );
    });

    return {
      title: dashboard.title,
      description: dashboard.description,
      slug: dashboard.slug,
      warehouseId: dashboard.warehouseId,
      warehouseName: wh?.name ?? 'Unknown',
      warehouseType: wh?.type ?? 'POSTGRES',
      params: paramValues,
      panels: panelSummaries,
    };
  }, [dashboard, paramValues, queryClient, warehouses, orpc.dashboard.query]);

  const sendMessage = useCallback(
    (content: string) => {
      if (!sessionId || !dashboard) return;
      const ctx = gatherDashboardContext();
      if (!ctx) return;

      addMessage({ type: 'user_message', content });
      setAgentActive(true);

      sendMessageMutation.mutate(
        { sessionId, content, dashboardContext: ctx },
        {
          onSuccess: (data) => {
            if (data.turnId) setActiveTurnId(data.turnId);
          },
          onError: (err: unknown) => {
            removeLastUserMessage(content);
            // TURN_IN_PROGRESS: another turn is already running (post-stop race).
            // Restore the prompt so the user can retry once the abort completes.
            // Reconnect to the active turn's SSE stream.
            const errData = (err as { data?: { code?: string; activeTurnId?: string } })?.data;
            if (errData?.code === 'TURN_IN_PROGRESS' && errData.activeTurnId) {
              setPrefillValue(content);
              setPrefillNonce((n) => n + 1);
              setActiveTurnId(errData.activeTurnId);
              return;
            }
            // Surface the error so the user knows the send failed.
            const msg = err instanceof Error ? err.message : 'Something went wrong. Please try again.';
            addMessage({ type: 'error', message: msg });
            setAgentActive(false);
            setActiveTurnId(null);
          },
        },
      );
    },
    [sessionId, dashboard, gatherDashboardContext, addMessage, removeLastUserMessage, sendMessageMutation],
  );

  // Reset mutation
  const resetMutation = useMutation(orpc.dashboardChat.reset.mutationOptions());

  const resetChat = useCallback(() => {
    if (!dashboard) return;
    resetMutation.mutate(
      { dashboardId: dashboard.id },
      {
        onSuccess: () => {
          setSessionId(null);
          clearMessages();
          initializedForSession.current = null;
          setAgentActive(false);
          setActiveTurnId(null);
          void refetchSession();
        },
      },
    );
  }, [dashboard, resetMutation, clearMessages, refetchSession]);

  const open = useCallback(() => setIsOpen(true), []);
  const close = useCallback(() => setIsOpen(false), []);
  const toggle = useCallback(() => setIsOpen((prev) => !prev), []);

  // Session is ready when getOrCreate has resolved
  const sessionReady = !!sessionId;

  // Queued message for openAndSend — bridges the gap when session isn't ready yet
  const queuedMessage = useRef<string | null>(null);

  const openAndSend = useCallback(
    (message: string) => {
      if (sessionReady && !agentActive) {
        open();
        sendMessage(message);
      } else {
        queuedMessage.current = message;
        open();
      }
    },
    [sessionReady, agentActive, open, sendMessage],
  );

  // Drain queued message once session is ready
  useEffect(() => {
    if (sessionReady && queuedMessage.current && !agentActive) {
      const msg = queuedMessage.current;
      queuedMessage.current = null;

      sendMessage(msg);
    }
  }, [sessionReady, agentActive, sendMessage]);

  return {
    isOpen,
    open,
    close,
    toggle,
    sessionId,
    messages,
    connected,
    error: sseError,
    sendMessage,
    resetChat,
    agentActive,
    sessionReady,
    openAndSend,
    stopTurn,
    canStop: agentActive && knownTurnId !== null,
    prefillValue,
    prefillNonce,
  };
}
